import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "../context/ThemeContext";
import { Sun, Moon, Menu, X } from "lucide-react";
import { useState } from "react";
import { NAV } from "../constants/testIds";

const LOGO_DARK = "https://customer-assets.emergentagent.com/job_delcielo-corp/artifacts/g54g7x2d_logo-web-dark.webp";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_delcielo-corp/artifacts/qdm4l4av_logo-web-light.webp";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/", label: t("nav.home"), tid: NAV.home },
    { to: "/capacitaciones", label: t("nav.capacitaciones"), tid: NAV.capacitaciones },
    { to: "/conferencias", label: t("nav.conferencias"), tid: NAV.conferencias },
    { to: "/consultorias", label: t("nav.consultorias"), tid: NAV.consultorias },
    { to: "/desarrollo", label: t("nav.desarrollo"), tid: NAV.desarrollo },
    { to: "/contacto", label: t("nav.contacto"), tid: NAV.contacto },
  ];

  const toggleLang = () => {
    const next = i18n.language?.startsWith("es") ? "en" : "es";
    i18n.changeLanguage(next);
  };

  const isEs = i18n.language?.startsWith("es");

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border">
      <div className="container-page flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2" onClick={() => setOpen(false)}>
          <img
            src={theme === "dark" ? LOGO_DARK : LOGO_LIGHT}
            alt="dc.TECH"
            className="h-9 w-auto"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.tid}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  isActive
                    ? "text-foreground bg-secondary"
                    : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleLang}
            data-testid={NAV.langToggle}
            className="hidden md:inline-flex items-center px-3 py-2 rounded-full text-xs font-semibold uppercase tracking-wider border border-border hover:border-primary transition-colors"
            aria-label="Toggle language"
          >
            <span className={isEs ? "text-foreground" : "text-muted-foreground"}>ES</span>
            <span className="mx-1.5 text-muted-foreground">|</span>
            <span className={!isEs ? "text-foreground" : "text-muted-foreground"}>EN</span>
          </button>

          <button
            type="button"
            onClick={toggle}
            data-testid={NAV.themeToggle}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-border hover:border-primary hover:text-primary transition-all"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          <button
            type="button"
            onClick={() => navigate("/contacto")}
            data-testid={NAV.cta}
            className="hidden md:inline-flex btn-primary text-sm px-5 py-2.5"
          >
            {t("nav.cta")}
          </button>

          <button
            type="button"
            data-testid={NAV.mobileMenu}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-border"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden border-t border-border bg-background">
          <nav className="container-page py-6 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`${l.tid}-mobile`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-base font-medium ${
                    isActive ? "bg-secondary text-foreground" : "text-muted-foreground"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-2 pt-4">
              <button
                onClick={toggleLang}
                data-testid={`${NAV.langToggle}-mobile`}
                className="flex-1 px-4 py-3 rounded-full text-sm font-semibold border border-border"
              >
                {isEs ? "ES | en" : "es | EN"}
              </button>
              <button
                onClick={() => { setOpen(false); navigate("/contacto"); }}
                className="flex-1 btn-primary justify-center"
              >
                {t("nav.cta")}
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
